import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Space,
  Tag,
  Typography,
  Descriptions,
  Row,
  Col,
  Statistic,
  Alert,
} from 'antd';
import {
  WarningOutlined,
  ReloadOutlined,
  EyeOutlined,
  UploadOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchDisputes,
  fetchDisputeDetail,
  submitDisputeEvidence,
  acceptDispute,
  challengeDispute,
} from '../store/slices/disputeSlice';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const DisputesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [evidenceModalVisible, setEvidenceModalVisible] = useState(false);
  const [selectedDisputeId, setSelectedDisputeId] = useState<string | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [filters, setFilters] = useState<any>({});

  const {
    disputes,
    currentDispute,
    submitting,
    error,
  } = useAppSelector((state) => state.dispute);

  useEffect(() => {
    dispatch(fetchDisputes({ page: 1, limit: 20 }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleViewDetail = async (id: string) => {
    try {
      await dispatch(fetchDisputeDetail(id)).unwrap();
      setDetailModalVisible(true);
    } catch (err: any) {
      message.error(err || '获取争议详情失败');
    }
  };

  const handleSubmitEvidence = async (values: any) => {
    if (!selectedDisputeId) return;

    try {
      await dispatch(
        submitDisputeEvidence({
          id: selectedDisputeId,
          evidence: {
            ...values,
            documents: fileList.map((f) => f.response?.url || f.url),
          },
        })
      ).unwrap();
      message.success('证据已提交');
      setEvidenceModalVisible(false);
      setSelectedDisputeId(null);
      form.resetFields();
      setFileList([]);
      dispatch(fetchDisputes({ page: 1, limit: 20 }));
    } catch (err: any) {
      message.error(err || '提交证据失败');
    }
  };

  const handleAcceptDispute = async (id: string) => {
    Modal.confirm({
      title: '确认接受争议？',
      content: '接受争议后，将退款给客户，此操作不可撤销。',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          await dispatch(acceptDispute(id)).unwrap();
          message.success('已接受争议');
          dispatch(fetchDisputes({ page: 1, limit: 20 }));
        } catch (err: any) {
          message.error(err || '接受争议失败');
        }
      },
    });
  };

  const handleChallengeDispute = async (id: string) => {
    Modal.confirm({
      title: '确认质疑争议？',
      content: '质疑争议后，需要提交相关证据支持您的主张。',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          await dispatch(challengeDispute(id)).unwrap();
          message.success('已质疑争议，请提交证据');
          setSelectedDisputeId(id);
          setEvidenceModalVisible(true);
        } catch (err: any) {
          message.error(err || '质疑争议失败');
        }
      },
    });
  };

  const handleRefresh = () => {
    dispatch(fetchDisputes({ page: 1, limit: 20, filters }));
  };

  const handleTableChange = (pagination: any, tableFilters: any) => {
    const newFilters = {
      status: tableFilters.status?.[0],
    };
    setFilters(newFilters);
    dispatch(
      fetchDisputes({
        page: pagination.current,
        limit: pagination.pageSize,
        filters: newFilters,
      })
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: '争议ID',
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: '支付ID',
      dataIndex: 'payment_id',
      key: 'payment_id',
      render: (text: string) => (
        <Text
          copyable
          type="secondary"
          style={{ fontFamily: 'monospace', fontSize: 12 }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: '争议金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Text strong type="danger">
          {record.currency} {(amount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: '待处理', value: 'requires_response' },
        { text: '审核中', value: 'under_review' },
        { text: '已胜诉', value: 'won' },
        { text: '已败诉', value: 'lost' },
        { text: '已接受', value: 'accepted' },
      ],
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          requires_response: { label: '待处理', color: 'warning' },
          under_review: { label: '审核中', color: 'processing' },
          won: { label: '已胜诉', color: 'success' },
          lost: { label: '已败诉', color: 'error' },
          accepted: { label: '已接受', color: 'default' },
        };
        const config = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason: string) => {
        const reasonMap: Record<string, string> = {
          fraudulent: '欺诈',
          unrecognized: '不认识',
          duplicate: '重复支付',
          product_not_received: '未收到商品',
          product_unacceptable: '商品不符',
          subscription_canceled: '订阅已取消',
          other: '其他',
        };
        return reasonMap[reason] || reason;
      },
    },
    {
      title: '响应截止',
      dataIndex: 'respond_by',
      key: 'respond_by',
      render: (text: string) => {
        const deadline = dayjs(text);
        const now = dayjs();
        const isUrgent = deadline.diff(now, 'day') <= 3;
        return (
          <Text type={isUrgent ? 'danger' : undefined}>
            {deadline.format('YYYY-MM-DD HH:mm:ss')}
          </Text>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record.id)}
          >
            详情
          </Button>
          {record.status === 'requires_response' && (
            <>
              <Button
                type="link"
                icon={<UploadOutlined />}
                onClick={() => {
                  setSelectedDisputeId(record.id);
                  setEvidenceModalVisible(true);
                }}
              >
                提交证据
              </Button>
              <Button
                type="link"
                icon={<CheckOutlined />}
                onClick={() => handleAcceptDispute(record.id)}
              >
                接受
              </Button>
              <Button
                type="link"
                danger
                icon={<CloseOutlined />}
                onClick={() => handleChallengeDispute(record.id)}
              >
                质疑
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const totalAmount =
    disputes.data?.reduce((sum, d) => sum + d.amount, 0) || 0;
  const needsResponseCount =
    disputes.data?.filter((d) => d.status === 'requires_response').length || 0;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          争议管理
        </Title>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
          刷新
        </Button>
      </div>

      {needsResponseCount > 0 && (
        <Alert
          message="注意"
          description={`您有 ${needsResponseCount} 个争议需要响应，请及时处理避免自动败诉。`}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总争议数"
              value={disputes.meta?.total || 0}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="待处理"
              value={needsResponseCount}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="争议总金额"
              value={totalAmount / 100}
              precision={2}
              suffix="USD"
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={disputes.data || []}
          loading={disputes.loading}
          rowKey="id"
          locale={{ emptyText: '暂无数据' }}
          onChange={handleTableChange}
          pagination={{
            total: disputes.meta?.total || 0,
            current: disputes.meta?.current_page || 1,
            pageSize: disputes.meta?.per_page || 20,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
        />
      </Card>

      <Modal
        title="争议详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={900}
      >
        {currentDispute && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="争议ID" span={2}>
              <Text copyable>{currentDispute.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="支付ID" span={2}>
              <Text copyable>{currentDispute.payment_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="争议金额">
              <Text strong type="danger">
                {currentDispute.currency} {(currentDispute.amount / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag
                color={
                  currentDispute.status === 'won'
                    ? 'success'
                    : currentDispute.status === 'lost'
                    ? 'error'
                    : 'warning'
                }
              >
                {currentDispute.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="争议原因" span={2}>
              {currentDispute.reason}
            </Descriptions.Item>
            <Descriptions.Item label="客户描述" span={2}>
              {currentDispute.customer_message || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="响应截止">
              {dayjs(currentDispute.respond_by).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(currentDispute.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            {currentDispute.evidence_submitted_at && (
              <Descriptions.Item label="证据提交时间">
                {dayjs(currentDispute.evidence_submitted_at).format(
                  'YYYY-MM-DD HH:mm:ss'
                )}
              </Descriptions.Item>
            )}
            {currentDispute.closed_at && (
              <Descriptions.Item label="关闭时间">
                {dayjs(currentDispute.closed_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            )}
            {currentDispute.evidence && (
              <Descriptions.Item label="提交的证据" span={2}>
                <pre style={{ margin: 0 }}>
                  {JSON.stringify(currentDispute.evidence, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="提交证据"
        open={evidenceModalVisible}
        onCancel={() => {
          setEvidenceModalVisible(false);
          setSelectedDisputeId(null);
          form.resetFields();
          setFileList([]);
        }}
        footer={null}
        width={700}
      >
        <Alert
          message="证据提交说明"
          description="请提供详细的证据材料，包括订单信息、物流单号、客户通信记录等。证据越充分，胜诉的可能性越高。"
          type="info"
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical" onFinish={handleSubmitEvidence}>
          <Form.Item
            label="客户名称"
            name="customer_name"
            rules={[{ required: true, message: '请输入客户名称' }]}
          >
            <Input placeholder="请输入客户名称" />
          </Form.Item>

          <Form.Item
            label="产品描述"
            name="product_description"
            rules={[{ required: true, message: '请输入产品描述' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入产品描述" />
          </Form.Item>

          <Form.Item label="物流单号" name="shipping_tracking_number">
            <Input placeholder="请输入物流单号（如有）" />
          </Form.Item>

          <Form.Item label="客户邮箱" name="customer_email_address">
            <Input placeholder="请输入客户邮箱" />
          </Form.Item>

          <Form.Item label="退款政策" name="refund_policy">
            <Input.TextArea rows={3} placeholder="请输入退款政策说明" />
          </Form.Item>

          <Form.Item label="补充说明" name="additional_comments">
            <Input.TextArea rows={4} placeholder="请输入补充说明" />
          </Form.Item>

          <Form.Item label="上传文件">
            <Upload
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              beforeUpload={() => false}
              multiple
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12 }}>
              支持上传订单截图、物流凭证、通信记录等证据材料
            </Text>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setEvidenceModalVisible(false);
                  setSelectedDisputeId(null);
                  form.resetFields();
                  setFileList([]);
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                提交证据
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DisputesPage;
